import imageCompression from 'browser-image-compression';

export const REVIEW_IMAGE_MAX_SIZE = 1 * 1024 * 1024;
export const REVIEW_VIDEO_MAX_SIZE = 5 * 1024 * 1024;
export const REVIEW_IMAGE_SOURCE_MAX_SIZE = 20 * 1024 * 1024;
export const REVIEW_VIDEO_SOURCE_MAX_SIZE = 50 * 1024 * 1024;

const IMAGE_MAX_DIMENSION = 1600;
const VIDEO_MAX_DIMENSION = 1280;
const FFMPEG_CORE_BASE_URL =
  'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd';

let ffmpegInstance;

// Android 웹뷰가 첫 번째 파일 URI를 준비할 시간을 파일 선택 직후 확보합니다.
const NATIVE_FILE_PICKER_SETTLE_DELAY = 350;

// Android 웹뷰에서 첫 파일 읽기 권한 반영이 늦는 경우 간격을 늘려 재시도합니다.
const NATIVE_FILE_READ_RETRY_DELAYS = [0, 250, 600, 1200, 2000];

// 네이티브 웹뷰에서는 파일 접근 권한이 Web Worker로 전달되지 않을 수 있습니다.
const isNativeWebView = () =>
  Boolean(
    window.ReactNativeWebView || navigator.userAgent.includes('JmWebViewAgent'),
  );

// 파일 읽기 재시도 사이에 브라우저가 URI 권한을 반영할 시간을 제공합니다.
const wait = (delay) =>
  new Promise((resolve) => {
    setTimeout(resolve, delay);
  });

// 네이티브 URI를 한 번만 읽어 후속 모듈이 사용할 메모리 File로 고정합니다.
const createReadableFile = async (file) => {
  const retryDelays = isNativeWebView() ? NATIVE_FILE_READ_RETRY_DELAYS : [0];
  let lastCause;

  for (const delay of retryDelays) {
    if (delay > 0) await wait(delay);

    try {
      const sourceBuffer = await file.arrayBuffer();

      if (file.size > 0 && sourceBuffer.byteLength === 0) {
        throw new Error('파일 데이터가 비어 있습니다.');
      }

      return {
        file: new File([sourceBuffer], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        }),
        bytes: new Uint8Array(sourceBuffer),
      };
    } catch (cause) {
      lastCause = cause;
    }
  }

  const error = new Error(
    '선택한 파일의 실제 데이터를 읽을 수 없습니다. 잠시 후 다시 선택해주세요.',
  );
  error.name = 'ReviewMediaReadError';
  error.stage = 'SOURCE_FILE_ARRAY_BUFFER';
  error.fileName = file.name;
  error.readAttempts = retryDelays.length;
  error.cause = lastCause;
  throw error;
};

// 처리 단계별 오류를 구분해 웹뷰 화면에서도 정확한 실패 원인을 확인합니다.
const createProcessingError = (file, stage, fallbackMessage, cause) => {
  const error = new Error(cause?.message || fallbackMessage);
  error.name = 'ReviewMediaProcessError';
  error.stage = stage;
  error.fileName = file.name;
  error.cause = cause;
  return error;
};

// 파일 유형과 원본 용량을 실제 데이터 읽기 전에 검사합니다.
const validateSourceFile = (file) => {
  if (file.type.startsWith('image/')) {
    if (file.size > REVIEW_IMAGE_SOURCE_MAX_SIZE) {
      throw createProcessingError(
        file,
        'SOURCE_VALIDATION',
        '이미지는 20MB 이하만 선택할 수 있습니다.',
      );
    }
    return;
  }

  if (file.type.startsWith('video/')) {
    if (file.size > REVIEW_VIDEO_SOURCE_MAX_SIZE) {
      throw createProcessingError(
        file,
        'SOURCE_VALIDATION',
        '동영상은 50MB 이하만 선택할 수 있습니다.',
      );
    }
    return;
  }

  throw createProcessingError(
    file,
    'SOURCE_VALIDATION',
    '이미지 또는 동영상 파일만 등록할 수 있습니다.',
  );
};

// 브라우저에서 영상 정보를 읽어 변환 여부와 목표 비트레이트를 계산합니다.
const getVideoMetadata = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
      URL.revokeObjectURL(objectUrl);
    };
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('동영상 정보를 확인할 수 없습니다.'));
    };
    video.src = objectUrl;
  });

// FFmpeg WASM은 영상이 선택된 경우에만 내려받아 초기 로딩 용량을 줄입니다.
const getFFmpeg = async () => {
  if (ffmpegInstance?.loaded) return ffmpegInstance;

  const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
    import('@ffmpeg/ffmpeg'),
    import('@ffmpeg/util'),
  ]);

  ffmpegInstance = ffmpegInstance || new FFmpeg();
  await ffmpegInstance.load({
    coreURL: await toBlobURL(
      `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`,
      'text/javascript',
    ),
    wasmURL: await toBlobURL(
      `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`,
      'application/wasm',
    ),
  });

  return ffmpegInstance;
};

// 이미지 크기와 용량을 브라우저에서 줄여 변환된 File을 반환합니다.
const processImage = async (file, onProgress) => {
  if (file.type === 'image/gif') {
    if (file.size > REVIEW_IMAGE_MAX_SIZE) {
      throw new Error('움직이는 GIF 이미지는 1MB 이하만 등록 가능합니다.');
    }
    return file;
  }

  const compressionOptions = {
    maxSizeMB: REVIEW_IMAGE_MAX_SIZE / (1024 * 1024),
    maxWidthOrHeight: IMAGE_MAX_DIMENSION,
    preserveExif: false,
    initialQuality: 0.85,
    onProgress,
  };

  let compressedFile;

  try {
    // 네이티브에서는 파일 권한을 유지할 수 있도록 메인 스레드에서 압축합니다.
    compressedFile = await imageCompression(file, {
      ...compressionOptions,
      useWebWorker: !isNativeWebView(),
    });
  } catch (error) {
    if (isNativeWebView()) throw error;

    // 일반 브라우저도 Worker 접근이 실패하면 메인 스레드에서 다시 시도합니다.
    compressedFile = await imageCompression(file, {
      ...compressionOptions,
      useWebWorker: false,
    });
  }

  if (compressedFile.size > REVIEW_IMAGE_MAX_SIZE) {
    throw new Error('이미지를 1MB 이하로 줄일 수 없습니다.');
  }

  // 압축 모듈이 반환한 Blob을 FileManager가 허용하는 실제 File로 변환합니다.
  return new File([compressedFile], file.name, {
    type: compressedFile.type || file.type,
    lastModified: file.lastModified,
  });
};

// 영상 해상도와 비트레이트를 조정해 MP4 파일로 변환합니다.
const processVideo = async (file, sourceBytes, onProgress) => {
  let metadata;

  try {
    metadata = await getVideoMetadata(file);
  } catch (error) {
    // 웹뷰가 영상 메타데이터를 읽지 못해도 용량 기준을 만족하면 원본을 사용합니다.
    if (file.size <= REVIEW_VIDEO_MAX_SIZE) return file;
    metadata = null;
  }

  const needsProcessing =
    file.size > REVIEW_VIDEO_MAX_SIZE ||
    Math.max(metadata?.width || 0, metadata?.height || 0) > VIDEO_MAX_DIMENSION;

  if (!needsProcessing) return file;

  const ffmpeg = await getFFmpeg();
  const inputExtension = file.name.split('.').pop()?.toLowerCase() || 'video';
  const uniqueId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const inputName = `review-input-${uniqueId}.${inputExtension}`;
  const outputName = `review-output-${uniqueId}.mp4`;
  const durationOutputName = `review-duration-${uniqueId}.txt`;
  const outputFileName = file.name.replace(/\.[^.]+$/, '') + '.mp4';
  const progressHandler = ({ progress }) => {
    onProgress(Math.min(100, Math.max(0, Math.round(progress * 100))));
  };

  ffmpeg.on('progress', progressHandler);

  try {
    // FFmpeg에는 FileReader 재호출 없이 미리 확보한 파일 바이트를 전달합니다.
    await ffmpeg.writeFile(inputName, sourceBytes);

    let duration = metadata?.duration;

    if (!Number.isFinite(duration) || duration <= 0) {
      // HTML video가 실패한 웹뷰에서는 FFmpeg가 영상 길이를 직접 확인합니다.
      const probeExitCode = await ffmpeg.ffprobe([
        '-v',
        'error',
        '-show_entries',
        'format=duration',
        '-of',
        'default=noprint_wrappers=1:nokey=1',
        inputName,
        '-o',
        durationOutputName,
      ]);

      if (probeExitCode !== 0) {
        throw new Error('동영상 재생 시간을 확인할 수 없습니다.');
      }

      const durationData = await ffmpeg.readFile(durationOutputName, 'utf8');
      duration = Number(String(durationData).trim());
    }

    if (!Number.isFinite(duration) || duration <= 0) {
      throw new Error('동영상 재생 시간을 확인할 수 없습니다.');
    }

    const totalBitrate = Math.floor(
      ((REVIEW_VIDEO_MAX_SIZE * 0.9 * 8) / duration) * 0.001,
    );
    const videoBitrate = Math.max(250, Math.min(totalBitrate - 96, 2500));

    const exitCode = await ffmpeg.exec([
      '-i',
      inputName,
      '-vf',
      `scale=${VIDEO_MAX_DIMENSION}:${VIDEO_MAX_DIMENSION}:force_original_aspect_ratio=decrease:force_divisible_by=2`,
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-b:v',
      `${videoBitrate}k`,
      '-maxrate',
      `${videoBitrate}k`,
      '-bufsize',
      `${videoBitrate * 2}k`,
      '-c:a',
      'aac',
      '-b:a',
      '96k',
      '-movflags',
      '+faststart',
      outputName,
    ]);

    if (exitCode !== 0) {
      throw new Error('동영상 변환에 실패했습니다.');
    }

    const outputData = await ffmpeg.readFile(outputName);
    const processedFile = new File([outputData.buffer], outputFileName, {
      type: 'video/mp4',
      lastModified: Date.now(),
    });

    if (processedFile.size > REVIEW_VIDEO_MAX_SIZE) {
      throw new Error('업로드를 실패했습니다.');
    }
    return processedFile;
  } finally {
    ffmpeg.off('progress', progressHandler);
    await Promise.allSettled([
      ffmpeg.deleteFile(inputName),
      ffmpeg.deleteFile(outputName),
      ffmpeg.deleteFile(durationOutputName),
    ]);
  }
};

// 선택된 리뷰 파일을 한 개씩 처리해 브라우저 메모리 사용량을 제한합니다.
export const processReviewMediaFiles = async (files, onStatus) => {
  const processedFiles = [];
  const failedFiles = [];

  // 네이티브 파일 스트림 충돌을 피하도록 원본 데이터를 순서대로 메모리에 확보합니다.
  onStatus(`첨부 파일 ${files.length}개 읽는 중`);
  const readableFiles = [];

  // 파일 선택 콜백 직후에는 첫 번째 URI 권한이 아직 준비되지 않을 수 있습니다.
  if (isNativeWebView()) {
    await wait(NATIVE_FILE_PICKER_SETTLE_DELAY);
  }

  for (let index = 0; index < files.length; index += 1) {
    const sourceFile = files[index];

    try {
      validateSourceFile(sourceFile);
      const readableFile = await createReadableFile(sourceFile);
      readableFiles.push({ ...readableFile, sourceFile, index });
    } catch (error) {
      // 원본 바이트 읽기가 실패한 이미지는 브라우저 이미지 디코더로 한 번 더 처리합니다.
      if (sourceFile.type.startsWith('image/')) {
        readableFiles.push({
          file: sourceFile,
          bytes: null,
          sourceFile,
          index,
          sourceReadError: error,
        });
        continue;
      }

      failedFiles.push({ file: sourceFile, error });
    }
  }

  for (const readableFile of readableFiles) {
    if (!readableFile) continue;

    const { file, bytes, sourceFile, index, sourceReadError } = readableFile;
    const current = index + 1;

    if (sourceFile.type.startsWith('image/')) {
      try {
        const processedFile = await processImage(file, (progress) => {
          onStatus(`이미지 ${current}/${files.length} 처리 중 (${progress}%)`);
        });
        processedFiles.push(processedFile);
      } catch (cause) {
        const processingError = createProcessingError(
          sourceFile,
          'IMAGE_PROCESSING',
          '이미지를 처리할 수 없습니다.',
          cause,
        );

        // 최초 원본 읽기 오류도 함께 보존해 네이티브 파일 접근 문제를 구분합니다.
        processingError.sourceReadError = sourceReadError;
        failedFiles.push({
          file: sourceFile,
          error: processingError,
        });
      }
      continue;
    }

    if (sourceFile.type.startsWith('video/')) {
      try {
        const processedFile = await processVideo(file, bytes, (progress) => {
          onStatus(`동영상 ${current}/${files.length} 처리 중 (${progress}%)`);
        });
        processedFiles.push(processedFile);
      } catch (cause) {
        failedFiles.push({
          file: sourceFile,
          error: createProcessingError(
            sourceFile,
            'VIDEO_PROCESSING',
            '동영상을 처리할 수 없습니다.',
            cause,
          ),
        });
      }
      continue;
    }
  }

  return { processedFiles, failedFiles };
};
